// A game, as two positions and a verdict.
//
// Midgame beside final on purpose — the midgame is where a factory either
// exists or does not, the final is how the argument ended, and either one alone
// is easy to misread. The same pairing `latest.txt` has always used, which is
// where the pictures came from.
//
// **Named `TunerGame`, not `GameCard`.** The kit has a `GameCard` and it is the
// deck's card; a second component of that name in this directory would be one
// import away from being drawn in a hand.

import { type TunerGame as Game } from "src/engine/net/tuner";
import { Btn, HoverCard, styled, Text } from "../kit";
import { gameCaption, readDuration, since, teamLabel } from "../tunerView";
import { TEAM_TONE } from "./palette";
import { Card, Row, Tag, tagSkin } from "./parts";

/** One board, as the tuner drew it. */
const Ascii = styled("pre")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.kit.font.display,
  fontSize: 11,
  lineHeight: 1.25,
  color: theme.kit.color.cream,
  whiteSpace: "pre",
  overflowX: "auto"
}));

/**
 * The card, with the winning team carried on an edge as well as in the badge,
 * so the feed can be scanned without reading every label.
 */
const Plate = styled(Card)({ borderLeftWidth: 5 });

const Boards = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.kit.space.lg
}));

const Half = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs
}));

/** A stated fact with a rule behind it — a duration, a repeat count. */
const Note = styled("span")(({ theme }) => ({
  padding: "1px 7px",
  borderRadius: theme.kit.radius.sm,
  border: `2px solid ${theme.kit.color.line}`,
  fontFamily: theme.kit.font.display,
  fontSize: 11,
  color: theme.kit.color.inkDim,
  cursor: "help"
}));

export function TunerGame({
  game,
  now,
  onShow
}: {
  game: Game;
  now: number;
  onShow(frame: "mid" | "final"): void;
}): React.ReactElement {
  const colour = TEAM_TONE[game.outcome];
  return (
    <Plate solid style={{ borderLeftColor: colour }}>
      <Row>
        <Tag style={tagSkin(colour)}>{teamLabel(game)}</Tag>
        <Text tone="dim" size={11}>
          gen {game.generation} · {game.island.split("-").slice(1).join("-") || game.island}
          {game.at > 0 ? ` · ${since(game.at, now)}` : ""}
        </Text>
        {typeof game.ms === "number" && game.ms > 0 && (
          <HoverCard
            placement="bottom"
            title={readDuration(game.ms)}
            card={
              `${game.plies} plies, ` +
              `${Math.round((game.ms / Math.max(1, game.plies)) * 10) / 10}ms a ply. ` +
              "Wall clock on whichever machine played it, so a pooled batch is timed on the " +
              "runner rather than on the orchestrator. Absent on games recorded before the " +
              "duration was."
            }
          >
            <Note>{readDuration(game.ms)}</Note>
          </HoverCard>
        )}
        {game.repeats > 1 && (
          <HoverCard
            placement="bottom"
            title={`×${game.repeats}`}
            card="Several candidates in this generation played this opening move for move the same way, so their games fold into one card. A high count means the generation has not diverged yet."
          >
            <Note>×{game.repeats}</Note>
          </HoverCard>
        )}
      </Row>

      <Boards>
        {game.mid && (
          <Half>
            <Row>
              <Text tone="dim" size={11}>
                midgame · ply {game.mid.ply}
              </Text>
              <Btn small variant="ghost" onClick={() => onShow("mid")}>
                jump to board
              </Btn>
            </Row>
            <Ascii>{game.mid.board.join("\n")}</Ascii>
          </Half>
        )}
        {game.final && (
          <Half>
            <Row>
              <Text tone="dim" size={11}>
                final · ply {game.final.ply}
              </Text>
              <Btn small variant="ghost" onClick={() => onShow("final")}>
                jump to board
              </Btn>
            </Row>
            <Ascii>{game.final.board.join("\n")}</Ascii>
          </Half>
        )}
      </Boards>

      {game.tally.length > 0 && <Ascii>{game.tally.join("\n")}</Ascii>}
      <Text block tone="dim" size={11}>
        {gameCaption(game)}
      </Text>
    </Plate>
  );
}
